import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, inject, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../core/auth/auth.service';
import { ApiError } from '../../core/auth/auth.models';
import { ToastService } from '../../core/ui/toast.service';

/**
 * Trocar a própria palavra-passe.
 *
 * <p>Não havia por onde: o menu do utilizador só tinha "Sair" e o login não oferecia nada. Quem
 * quisesse mudar a senha — ou achasse que alguém a tinha visto — ficava à espera de que um
 * administrador reenviasse o convite.
 */
@Component({
  selector: 'app-change-password-dialog',
  imports: [ReactiveFormsModule],
  template: `
    <div class="confirm-backdrop" (click)="close.emit()">
      <div class="confirm-panel" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
        <form [formGroup]="form" (ngSubmit)="submit()">
          <div class="confirm-body">
            <span class="confirm-icon"><i class="fe fe-lock"></i></span>
            <div class="flex-grow-1">
              <h4 class="mb-1">Alterar palavra-passe</h4>
              <p class="text-muted small mb-3">
                Precisamos da atual para confirmar que é mesmo você.
              </p>

              <label class="form-label small">Palavra-passe atual</label>
              <input class="form-control form-control-sm mb-1" type="password"
                     autocomplete="current-password" formControlName="currentPassword" />
              @if (erroDe('currentPassword'); as erro) {
                <small class="text-danger d-block mb-2">{{ erro }}</small>
              }

              <label class="form-label small mt-2">Nova palavra-passe</label>
              <input class="form-control form-control-sm mb-1" type="password"
                     autocomplete="new-password" formControlName="newPassword" />
              <small class="text-muted d-block">Pelo menos 8 caracteres.</small>
              @if (erroDe('newPassword'); as erro) {
                <small class="text-danger d-block">{{ erro }}</small>
              }

              <label class="form-label small mt-2">Repetir a nova</label>
              <input class="form-control form-control-sm mb-1" type="password"
                     autocomplete="new-password" formControlName="repeat" />

              @if (serverError(); as erro) {
                <div class="alert alert-danger py-2 small mt-3 mb-0">{{ erro }}</div>
              }
            </div>
          </div>
          <div class="confirm-actions">
            <button type="button" class="btn btn-sm btn-white" (click)="close.emit()">Cancelar</button>
            <button type="submit" class="btn btn-sm btn-primary" [disabled]="saving()">
              {{ saving() ? 'A guardar…' : 'Alterar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
})
export class ChangePasswordDialog {
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly toasts = inject(ToastService);

  readonly close = output<void>();

  protected readonly saving = signal(false);
  protected readonly serverError = signal<string | null>(null);
  /** Mensagens que o servidor devolveu, campo a campo. */
  private readonly fieldErrors = signal<Record<string, string>>({});

  protected readonly form = this.fb.nonNullable.group({
    currentPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, Validators.minLength(8)]],
    repeat: ['', Validators.required],
  });

  protected erroDe(campo: 'currentPassword' | 'newPassword'): string | null {
    const doServidor = this.fieldErrors()[campo];
    if (doServidor) return doServidor;
    const control = this.form.controls[campo];
    if (!control.touched || control.valid) return null;
    if (control.hasError('required')) {
      return campo === 'currentPassword'
        ? 'Indique a palavra-passe atual.'
        : 'Indique a palavra-passe nova.';
    }
    return 'A palavra-passe nova tem de ter pelo menos 8 caracteres.';
  }

  protected submit(): void {
    this.serverError.set(null);
    this.fieldErrors.set({});
    const { currentPassword, newPassword, repeat } = this.form.getRawValue();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (newPassword !== repeat) {
      this.serverError.set('A nova palavra-passe e a repetição não são iguais.');
      return;
    }
    this.saving.set(true);
    this.http.post<void>(`/api/t/${this.auth.currentTenant()}/me/palavra-passe`,
      { currentPassword, newPassword }).subscribe({
      next: () => {
        this.saving.set(false);
        this.toasts.success('Palavra-passe alterada.');
        this.close.emit();
      },
      error: (response: HttpErrorResponse) => {
        this.saving.set(false);
        const body = response.error as ApiError | undefined;
        const campos: Record<string, string> = {};
        for (const field of body?.fieldErrors ?? []) {
          campos[field.field] = field.message;
        }
        this.fieldErrors.set(campos);
        this.serverError.set(Object.keys(campos).length
          ? null
          : body?.message ?? 'Não foi possível alterar a palavra-passe.');
      },
    });
  }
}
